import { useSyncExternalStore } from 'react';

/****************
 * WHY SOMEONE CAME, AND THE GUIDE THAT FOLLOWS. Mishka, 2026-10-21.
 *
 * A new account first says what it came for, among five choices. The choice
 * picks the welcome video, and the video hands over to a guide that DOES the
 * thing with them, step by step: build a first strategy, find and copy a
 * trader, or connect their own AI. "I don't know" and "I want everything" keep
 * exactly what a new account was shown before: the tutorial video, then Quick
 * Learn.
 *
 * This file is the state all of that shares, and nothing that draws:
 *
 *   - the choice, kept on the Clerk account (unsafeMetadata.onboarding) like
 *     tutorialVideoSeen, mirrored in this browser in case the write fails;
 *   - a bus of small facts the pages emit ("a strategy was saved", "a trader
 *     is chosen") that a guide step can wait for, instead of asking Next;
 *   - whether the chooser, the video or a guide is on screen, so the Orb and
 *     the other first-visit cards keep quiet while it is;
 *   - where each guide got to, so a reload resumes it.
 *
 * Behind a preview switch until it is shown and agreed: ?onboarding=1 in the
 * address turns it on for this browser, ?onboarding=0 off. With it off,
 * everything behaves exactly as before.
 ****************/

import { inSpeedRun, onboardingIdFor } from '@/lib/speedRun';
export const INTENTS = ['copy_trading', 'strategy_builder', 'ai_trading', 'dont_know', 'want_everything'] as const;
export type Intent = typeof INTENTS[number];
/** The chooser's three, and first_run: Get Started on the real Lite pages (10-31). */
export type GoalIntent = 'copy_trading' | 'strategy_builder' | 'ai_trading' | 'first_run';

export const isIntent = (v: unknown): v is Intent => typeof v === 'string' && (INTENTS as readonly string[]).includes(v);
export const isGoal = (v: unknown): v is GoalIntent => v === 'copy_trading' || v === 'strategy_builder' || v === 'ai_trading';

/* ── The preview switch ─────────────────────────────────────────────────── */

// On for every new account since Mishka's go (2026-10-22).
export const ONBOARDING_FOR_EVERYONE = true;
const PREVIEW_KEY = 'copyfi.onboarding.preview';

let previewRead = false;
let preview = false;
export function onboardingEnabled(): boolean {
  if (ONBOARDING_FOR_EVERYONE) return true;
  if (previewRead) return preview;
  previewRead = true;
  try {
    const p = new URLSearchParams(window.location.search).get('onboarding');
    if (p === '1') localStorage.setItem(PREVIEW_KEY, '1');
    else if (p === '0') localStorage.removeItem(PREVIEW_KEY);
    preview = localStorage.getItem(PREVIEW_KEY) === '1';
  } catch { preview = false; }
  return preview;
}

/* ── Events between the pieces ──────────────────────────────────────────── */

/** Open the chooser (the "Tutorial video" buttons, once the switch is on). */
export const CHOOSER_EVENT = 'copyfi:onboarding:choose';
/** Start a guide: detail { intent, fresh? }. */
export const GUIDE_EVENT = 'copyfi:onboarding:guide';
/** A page saw something a guide may be waiting for: detail { signal, data }. */
export const ONBOARDING_EVENT = 'copyfi:onboarding';
const ACTIVE_EVENT = 'copyfi:onboarding:active';

export const openIntentChooser = () => window.dispatchEvent(new Event(CHOOSER_EVENT));
export const startIntentGuide = (intent: GoalIntent, fresh = true) =>
  window.dispatchEvent(new CustomEvent(GUIDE_EVENT, { detail: { intent, fresh } }));

/****************
 * WHAT THE PAGES TELL A GUIDE.
 *
 *   sb_step           the builder is on step `step` (0 Template … 5 Review)
 *   strategy_saved    Create Model succeeded: { name }
 *   ct_state          the copy-trade screen: StartState plus useAI and
 *                     aiAvailable (a key added shows as exchangesConnected)
 *   session_started   a session started
 *   ai_status         /ai: whether letting the AI decide is open: { available }
 *   token_created     /ai: a token was issued
 *   token_copied      /ai: Copy, or "I have saved it"
 *   token_connected   /ai: how many tokens show Connected: { count }
 *   copy_trader       a trader page: Copy Trader pressed
 *   dash_period       the Dashboard's Top Traders: the person picked a period
 *   dash_sort         ... changed the order (the menu or a column header)
 *   dash_filter       ... set a filter
 *
 * The last value of each is kept, so a guide that arrives after the fact
 * (a reload, a step reached late) still sees it.
 ****************/
export type OnboardingSignal =
  | 'sb_step' | 'strategy_saved' | 'ct_state' | 'session_started' | 'ai_status'
  | 'token_created' | 'token_copied' | 'token_connected' | 'copy_trader'
  | 'dash_period' | 'dash_sort' | 'dash_filter'
  /* Copy Trade's tab (live | backtest), and a backtest started (10-31). */
  | 'ct_tab' | 'backtest_started';

export interface CtState {
  traderChosen: boolean;
  strategyChosen: boolean;
  strategiesSaved: number;
  destinationChosen: boolean;
  exchangesConnected: number;
  useAI: boolean;
  aiAvailable: boolean;
}

const last = new Map<OnboardingSignal, unknown>();

export function emitOnboarding(signal: OnboardingSignal, data: unknown = true): void {
  last.set(signal, data);
  try {
    window.dispatchEvent(new CustomEvent(ONBOARDING_EVENT, { detail: { signal, data } }));
  } catch { /* no window: a test */ }
}

export function lastOnboarding<T = unknown>(signal: OnboardingSignal): T | undefined {
  return last.get(signal) as T | undefined;
}

/** For a page that unmounts: what it said no longer describes the screen. */
export function forgetOnboarding(signal: OnboardingSignal): void {
  last.delete(signal);
}

/* ── Quiet while it is on screen ────────────────────────────────────────── */

const active = new Set<string>();
const listeners = new Set<() => void>();

/** `who` is chooser, video or guide: each says it is on screen and when it is gone. */
export function setOnboardingActive(who: string, on: boolean): void {
  const had = active.has(who);
  if (on === had) return;
  if (on) active.add(who); else active.delete(who);
  listeners.forEach((l) => l());
  try { window.dispatchEvent(new Event(ACTIVE_EVENT)); } catch { /* no window */ }
}

export function isOnboardingActive(): boolean {
  return active.size > 0;
}

const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };

/** True while the chooser, a welcome video or a guide is on screen. */
export function useOnboardingActive(): boolean {
  return useSyncExternalStore(subscribe, isOnboardingActive, () => false);
}

/* ── What is kept on the account ────────────────────────────────────────── */

export type GuideEnd = 'finished' | 'abandoned';

export interface OnboardingMeta {
  intentFirst?: Intent;
  intentLast?: Intent;
  chooserSeen?: boolean;
  chosenAt?: string;
  guides?: Partial<Record<GoalIntent, GuideEnd>>;
}

export interface GuideProgress {
  intent: GoalIntent;
  step: number;
}

interface LocalState {
  meta?: OnboardingMeta;
  progress?: GuideProgress | null;
}

// Per person: the id is in the key, so sign-out forgets it by prefix (browserState.ts).
export const ONBOARDING_USER_PREFIX = 'copyfi.onboard.user';
const localKey = (userId: string) => `${ONBOARDING_USER_PREFIX}:${userId}`;

function readLocal(userId: string): LocalState {
  try {
    const raw = localStorage.getItem(localKey(onboardingIdFor(userId)));
    return raw ? (JSON.parse(raw) as LocalState) : {};
  } catch { return {}; }
}

function writeLocal(userId: string, patch: LocalState): void {
  try {
    localStorage.setItem(localKey(onboardingIdFor(userId)), JSON.stringify({ ...readLocal(userId), ...patch }));
  } catch { /* private mode: the Clerk copy still holds it */ }
}

/** The Clerk user, as much of it as this file needs. */
export interface OnboardingUser {
  id: string;
  createdAt?: Date | null;
  unsafeMetadata?: Record<string, unknown>;
  update: (p: { unsafeMetadata: Record<string, unknown> }) => Promise<unknown>;
}

export function readMeta(user: OnboardingUser): OnboardingMeta {
  // A speed run starts from nothing and never reads or writes the account's copy (lib/speedRun.ts).
  const remote = (inSpeedRun() ? {} : user.unsafeMetadata?.onboarding ?? {}) as OnboardingMeta;
  const local = readLocal(user.id).meta ?? {};
  return {
    ...local,
    ...remote,
    chooserSeen: remote.chooserSeen === true || local.chooserSeen === true,
    guides: { ...(local.guides ?? {}), ...(remote.guides ?? {}) },
  };
}

export function saveMeta(user: OnboardingUser, patch: OnboardingMeta): OnboardingMeta {
  const next = { ...readMeta(user), ...patch };
  if (patch.guides) next.guides = { ...(readMeta(user).guides ?? {}), ...patch.guides };
  writeLocal(user.id, { meta: next });
  if (!inSpeedRun()) user.update({ unsafeMetadata: { ...user.unsafeMetadata, onboarding: next } }).catch(() => {});
  if (next.intentLast) currentIntent = next.intentLast;
  return next;
}

export function readProgress(userId: string): GuideProgress | null {
  return readLocal(userId).progress ?? null;
}

export function saveProgress(userId: string, p: GuideProgress | null): void {
  writeLocal(userId, { progress: p });
}

const NEW_ACCOUNT_MS = 24 * 60 * 60 * 1000;

/** Same rule as the tutorial video: an account under a day old that has not seen the chooser. */
export function chooserIsDue(user: OnboardingUser, now = Date.now()): boolean {
  if (inSpeedRun()) return !readMeta(user).chooserSeen; // a newcomer whatever the account's age
  const created = user.createdAt ? new Date(user.createdAt).getTime() : 0;
  if (!created || now - created > NEW_ACCOUNT_MS) return false;
  return !readMeta(user).chooserSeen;
}

/* ── For the Orb ────────────────────────────────────────────────────────── */

let currentIntent: Intent | null = null;

export function rememberIntent(i: Intent | null | undefined): void {
  currentIntent = i ?? null;
}

const INTENT_WORDS: Record<Intent, string> = {
  copy_trading: 'finding a trader and copying them',
  strategy_builder: 'building their own strategy',
  ai_trading: 'connecting their own AI',
  dont_know: 'they did not know yet',
  want_everything: 'everything',
};

/** One English sentence for the Orb's context (read by the model, never shown). */
export function orbIntentLine(): string {
  return currentIntent ? ` They told us they came to CopyFi for: ${INTENT_WORDS[currentIntent]}.` : '';
}

/****************
 * WHO HAS THE FIRST SCREEN.
 *
 * Quick Learn starts by itself on a browser that has not seen it. With the
 * chooser on, a new account's first screen is the chooser, and Quick Learn
 * comes after the general video if they pick "I don't know" or "I want
 * everything" -- never over the chooser, and never for the three goals. So
 * Quick Learn asks here first, and the chooser answers once Clerk has loaded:
 * claimed (the chooser is due or was seen, or a guide is to resume) or not
 * (an account from before it: everything as before).
 ****************/
let firstVisit: boolean | null = null;
const firstVisitWaiters: ((claimed: boolean) => void)[] = [];

export function decideFirstVisit(claimed: boolean): void {
  if (firstVisit !== null) return;
  firstVisit = claimed;
  firstVisitWaiters.splice(0).forEach((w) => w(claimed));
}

export function whenFirstVisitDecided(cb: (claimed: boolean) => void): () => void {
  if (firstVisit !== null) { cb(firstVisit); return () => {}; }
  firstVisitWaiters.push(cb);
  return () => {
    const i = firstVisitWaiters.indexOf(cb);
    if (i >= 0) firstVisitWaiters.splice(i, 1);
  };
}
